import type {
  CatalogItemRecord,
  ClientRecord,
  CommentRecord,
  DossierFileRecord,
  InterventionRecord,
  PaymentMethodRecord,
  PaymentRecord,
  SavRecord,
  PortalLinkRecord,
  ProspectRecord,
  DossierLineRecord,
  DossierRecord,
  EntityRecordMap,
  Mutation,
  SyncEntity,
} from '@clim/domain';
import {
  SAV_USER_TRANSITIONS,
  currentPrice,
  htOfTtc,
  initialPaymentStatus,
  lineSnapshot,
  statusAfter,
} from '@clim/domain';
import { MUTATION_ENTITY, dossierMachine } from '@clim/domain';

export interface LocalActor {
  userId: string;
  displayName: string;
  /** dossier.read scope of the user: an `own` creator becomes the commercial (as on the server) */
  dossierReadScope?: 'own' | 'territory' | 'all';
  /** peut valider un SAV (création directement « À planifier ») */
  canValidateSav?: boolean;
}

/**
 * Local, optimistic application of a mutation: what the user sees before the server
 * confirms. The server remains the only authority; the pulled record replaces this view.
 */
export function applyOptimistic<E extends SyncEntity>(
  entity: E,
  current: EntityRecordMap[E] | undefined,
  m: Mutation,
  actor: LocalActor,
  lookup: {
    client(id: string): ClientRecord | undefined;
    dossier?(id: string): DossierRecord | undefined;
    item?(id: string): CatalogItemRecord | undefined;
    brandLabel?(id: string): string | null;
    userName?(id: string): string | null;
    method?(id: string): PaymentMethodRecord | undefined;
  },
): EntityRecordMap[E] | undefined {
  if (MUTATION_ENTITY[m.type] !== entity) return current;
  const now = new Date().toISOString();
  switch (m.type) {
    case 'client.create': {
      const p = m.payload;
      const rec: ClientRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        territoryId: p.territoryId,
        kind: p.kind,
        civility: p.civility ?? null,
        firstName: p.firstName ?? null,
        lastName: p.lastName ?? null,
        companyName: p.companyName ?? null,
        siret: p.siret || null,
        email: p.email ? String(p.email).trim().toLowerCase() : null,
        phone: p.phone ?? null,
        phoneAlt: p.phoneAlt ?? null,
        addressLine1: p.addressLine1 ?? null,
        addressLine2: p.addressLine2 ?? null,
        postalCode: p.postalCode ?? null,
        city: p.city ?? null,
        notes: p.notes ?? null,
        marketingConsent: p.marketingConsent ?? false,
        contactName: p.contactName ?? null,
        extraPhones: (p.extraPhones ?? []).map((x) => ({ label: x.label ?? null, number: x.number })),
        customFields: p.customFields ?? {},
        ownerUserId: actor.userId,
        partnerId: p.partnerId ?? null,
        createdBy: actor.userId,
      };
      return rec as EntityRecordMap[E];
    }
    case 'dossier.create': {
      const p = m.payload;
      const client = lookup.client(p.clientId);
      const selfCommercial = actor.dossierReadScope !== 'all' && actor.dossierReadScope !== 'territory';
      const rec: DossierRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        reference: null,
        territoryId: p.territoryId ?? client?.territoryId ?? '',
        clientId: p.clientId,
        dossierTypeId: p.dossierTypeId,
        partnerId: p.partnerId === undefined ? (client?.partnerId ?? null) : p.partnerId,
        title: p.title ?? null,
        description: p.description ?? null,
        status: dossierMachine.initial,
        statusChangedAt: now,
        statusReasonId: null,
        siteAddressLine1: p.siteAddressLine1 ?? null,
        siteAddressLine2: p.siteAddressLine2 ?? null,
        sitePostalCode: p.sitePostalCode ?? null,
        siteCity: p.siteCity ?? null,
        geoLat: p.geoLat ?? null,
        geoLng: p.geoLng ?? null,
        geoAccuracyM: p.geoAccuracyM ?? null,
        geoCapturedAt: p.geoCapturedAt ?? null,
        installWallTypeId: p.installWallTypeId ?? null,
        installAtHeight: p.installAtHeight ?? null,
        installAccess: p.installAccess ?? null,
        installHeightM: p.installHeightM ?? null,
        reserves: (p.reserves ?? []).map((r) => ({ textId: r.textId, comment: r.comment ?? null })),
        saleChannel: p.saleChannel ?? 'off_premises',
        proEmployeeCount: p.proEmployeeCount ?? null,
        proOutsideMainActivity: p.proOutsideMainActivity ?? null,
        earlyStartRequested: p.earlyStartRequested ?? false,
        signature: null,
        submittedAt: null,
        lastControl: null,
        createdBy: actor.userId,
        assignments: selfCommercial
          ? [{ userId: actor.userId, userName: actor.displayName, role: 'commercial', assignedAt: now }]
          : [],
      };
      return rec as EntityRecordMap[E];
    }
    case 'client.update':
    case 'dossier.update': {
      if (!current) return current;
      const next = { ...current } as Record<string, unknown>;
      for (const [field, change] of Object.entries(m.changes)) next[field] = change.to;
      next.updatedAt = now;
      return next as unknown as EntityRecordMap[E];
    }
    case 'dossier.sign': {
      if (!current) return current;
      const p = m.payload;
      return {
        ...current,
        signature: {
          id: p.signatureId,
          method: 'onsite',
          signerName: p.signerName,
          signedAt: p.signedAt,
          contentHash: p.contentHash,
          fileId: null,
          totalTtcCents: null,
        },
      } as EntityRecordMap[E];
    }
    case 'dossier.void_signature':
      return current ? ({ ...current, signature: null } as EntityRecordMap[E]) : current;
    case 'dossier.transition': {
      if (!current) return current;
      const d = current as DossierRecord;
      return {
        ...d,
        status: m.payload.to,
        statusChangedAt: now,
        statusReasonId: m.payload.reasonId ?? null,
      } as EntityRecordMap[E];
    }
    case 'dossier.assign': {
      if (!current) return current;
      const d = current as DossierRecord;
      const others = d.assignments.filter(
        (a) =>
          !(a.userId === m.payload.userId && a.role === m.payload.role) &&
          !(m.payload.role === 'commercial' && a.role === 'commercial'),
      );
      return {
        ...d,
        assignments: [
          ...others,
          { userId: m.payload.userId, userName: '…', role: m.payload.role, assignedAt: now },
        ],
      } as EntityRecordMap[E];
    }
    case 'dossier.unassign': {
      if (!current) return current;
      const d = current as DossierRecord;
      return {
        ...d,
        assignments: d.assignments.filter(
          (a) => !(a.userId === m.payload.userId && a.role === m.payload.role),
        ),
      } as EntityRecordMap[E];
    }
    case 'comment.create': {
      const p = m.payload;
      const rec: CommentRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        dossierId: p.dossierId,
        visibility: p.visibility,
        body: p.body,
        authorId: actor.userId,
        authorName: actor.displayName,
      };
      return rec as EntityRecordMap[E];
    }
    case 'line.create': {
      // Estimation locale (catalogue synchronisé) ; le serveur fixe le prix définitif.
      const p = m.payload;
      const item = lookup.item?.(p.itemId);
      const dossier = lookup.dossier?.(p.dossierId);
      const price = item && dossier ? currentPrice(item.prices, dossier.territoryId) : undefined;
      // même désignation que celle que le serveur figera (variante choisie)
      const snap = item ? lineSnapshot(item, item.brandId ? lookup.brandLabel?.(item.brandId) : null) : null;
      // même calcul que le serveur : TTC exact saisi (administrateur) ou celui du tarif
      const vat = price?.vatRateBp ?? 0;
      const unitTtc =
        p.unitPriceTtcCents ?? (p.unitPriceHtCents != null ? null : (price?.priceTtcCents ?? null));
      const unitHt =
        p.unitPriceTtcCents != null
          ? htOfTtc(p.unitPriceTtcCents, vat)
          : (p.unitPriceHtCents ?? price?.priceHtCents ?? 0);
      const discountTtc = unitTtc != null && p.discountTtcCents ? p.discountTtcCents : null;
      const rec: DossierLineRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        dossierId: p.dossierId,
        itemId: p.itemId,
        sortOrder: Number.MAX_SAFE_INTEGER,
        quantity: p.quantity,
        label: snap?.label ?? 'Article',
        model: snap?.model ?? null,
        unit: item?.unit ?? 'unit',
        optionSteps: snap?.optionSteps ?? [],
        quoteHighlight: snap?.quoteHighlight ?? false,
        unitPriceHtCents: unitHt,
        unitPriceTtcCents: unitTtc,
        // ancien prix barré : celui du prix propre au dossier (administrateur), sinon celui du catalogue
        compareAtPriceHtCents:
          p.unitPriceTtcCents != null || p.unitPriceHtCents != null
            ? p.compareAtPriceTtcCents != null
              ? htOfTtc(p.compareAtPriceTtcCents, vat)
              : (p.compareAtPriceHtCents ?? null)
            : (price?.compareAtPriceHtCents ?? null),
        compareAtPriceTtcCents:
          p.unitPriceTtcCents != null || p.unitPriceHtCents != null
            ? (p.compareAtPriceTtcCents ?? null)
            : (price?.compareAtPriceTtcCents ?? null),
        priceOverridden: unitHt !== price?.priceHtCents,
        vatRateBp: price?.vatRateBp ?? 0,
        saleCommissionCents: null,
        installRemunerationCents: null,
        frozenAt: null,
        substitution: null,
        notes: p.notes ?? null,
        discountHtCents: discountTtc != null ? htOfTtc(discountTtc, vat) : (p.discountHtCents ?? 0),
        discountTtcCents: discountTtc,
        discountKind: p.discountKind ?? null,
        discountNote: p.discountNote ?? null,
      };
      return rec as EntityRecordMap[E];
    }
    case 'file.create': {
      const p = m.payload as Record<string, unknown>;
      const rec: DossierFileRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        dossierId: String(p.dossierId),
        kind: p.kind as DossierFileRecord['kind'],
        categoryId: (p.categoryId as string | null) ?? null,
        name: (p.name as string | undefined) ?? '',
        originalFilename: (p.originalFilename as string | null) ?? null,
        mime: p.mime as DossierFileRecord['mime'],
        sizeBytes: Number(p.sizeBytes),
        sha256: String(p.sha256),
        status: 'pending',
        source: 'app',
        createdBy: actor.userId,
        createdByName: actor.displayName,
      };
      return rec as EntityRecordMap[E];
    }
    case 'portal.create': {
      const p = m.payload;
      const rec: PortalLinkRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        dossierId: p.dossierId,
        allowEdit: p.allowEdit ?? true,
        allowUpload: p.allowUpload ?? true,
        allowSign: p.allowSign ?? true,
        channel: p.channel ?? null,
        expiresAt: p.expiresAt,
        createdBy: actor.userId,
        createdByName: actor.displayName,
        createdAt: now,
        revokedAt: null,
        lastUsedAt: null,
        useCount: 0,
      };
      return rec as EntityRecordMap[E];
    }
    case 'intervention.create': {
      const p = m.payload;
      const d = lookup.dossier?.(p.dossierId);
      const rec: InterventionRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        dossierId: p.dossierId,
        territoryId: d?.territoryId ?? '',
        status: 'to_assign',
        technicianId: null,
        technicianName: null,
        coTechnicianIds: [],
        coTechnicianNames: [],
        clientContactedAt: null,
        clientContactedByName: null,
        scheduledDate: p.scheduledDate ?? null,
        slotStart: p.slotStart ?? null,
        slotEnd: p.slotEnd ?? null,
        needsNacelle: p.needsNacelle ?? d?.installAccess === 'nacelle',
        city: d?.siteCity ?? null,
        postalCode: d?.sitePostalCode ?? null,
        activity: null,
        notes: p.notes ?? null,
        statusReasonId: null,
        statusComment: null,
        proposedAt: null,
        acceptedAt: null,
        takenChargeAt: null,
        onSiteAt: null,
        completedAt: null,
        completedById: null,
        completedByName: null,
        savId: p.savId ?? null,
      };
      return rec as EntityRecordMap[E];
    }
    case 'intervention.update': {
      if (!current) return current;
      const next = { ...current } as Record<string, unknown>;
      for (const [field, change] of Object.entries(m.changes)) next[field] = change.to;
      return next as unknown as EntityRecordMap[E];
    }
    case 'intervention.appointment': {
      if (!current) return current;
      const p = m.payload;
      const cur = current as unknown as InterventionRecord;
      const next: InterventionRecord = {
        ...cur,
        status: 'scheduled',
        scheduledDate: p.scheduledDate,
        slotStart: p.slotStart,
        slotEnd: p.slotEnd ?? null,
        acceptedAt: cur.acceptedAt ?? now,
        clientContactedAt: now,
        clientContactedByName: actor.displayName,
      };
      return next as unknown as EntityRecordMap[E];
    }
    case 'intervention.transition': {
      if (!current) return current;
      const p = m.payload;
      const cur = current as unknown as InterventionRecord;
      const next: InterventionRecord = {
        ...cur,
        status: p.to,
        statusReasonId: p.reasonId ?? null,
        statusComment: p.comment ?? null,
      };
      if (p.to === 'proposed')
        Object.assign(next, {
          technicianId: p.technicianId ?? null,
          technicianName: lookup.userName?.(p.technicianId ?? '') ?? null,
          proposedAt: now,
          scheduledDate: p.scheduledDate ?? cur.scheduledDate,
          slotStart: p.slotStart ?? cur.slotStart,
          slotEnd: p.slotEnd ?? cur.slotEnd,
        });
      if (p.to === 'to_assign') Object.assign(next, { technicianId: null, technicianName: null });
      if (p.to === 'accepted') next.acceptedAt = now;
      if (p.to === 'taken_charge') next.takenChargeAt = now;
      if (p.to === 'on_site') next.onSiteAt = now;
      if (p.to === 'completed')
        Object.assign(next, {
          completedAt: now,
          completedById: actor.userId,
          completedByName: actor.displayName,
        });
      return next as unknown as EntityRecordMap[E];
    }
    case 'payment.create': {
      const p = m.payload;
      const d = lookup.dossier?.(p.dossierId);
      const method = lookup.method?.(p.methodId);
      const kind = method?.kind ?? 'other';
      const rec: PaymentRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        dossierId: p.dossierId,
        territoryId: d?.territoryId ?? '',
        methodId: p.methodId,
        methodKind: kind,
        methodLabel: method?.label ?? '…',
        isDeposit: p.isDeposit ?? false,
        amountCents: p.amountCents,
        refundedCents: 0,
        status: method ? initialPaymentStatus(method) : 'pending',
        receivedOn: kind === 'stripe' ? null : (p.receivedOn ?? now.slice(0, 10)),
        reference: p.reference ?? null,
        comment: p.comment ?? null,
        collectedById: actor.userId,
        collectedByName: actor.displayName,
        confirmedById: null,
        confirmedByName: null,
        confirmedAt: null,
        statusReasonId: null,
        statusComment: null,
        checkoutUrl: null,
        checkoutExpiresAt: null,
        checkoutError: null,
        replacedByPaymentId: null,
        replacesPaymentId: null,
      };
      return rec as EntityRecordMap[E];
    }
    case 'payment.transition': {
      if (!current) return current;
      const p = m.payload;
      const cur = current as unknown as PaymentRecord;
      const next: PaymentRecord = {
        ...cur,
        status: p.to,
        statusReasonId: p.reasonId ?? null,
        statusComment: p.comment ?? null,
      };
      if (p.to === 'succeeded')
        Object.assign(next, {
          confirmedById: actor.userId,
          confirmedByName: actor.displayName,
          confirmedAt: now,
        });
      if (p.to === 'refunded') next.refundedCents = cur.amountCents;
      if (p.to === 'partially_refunded' && p.refundedCents != null) next.refundedCents = p.refundedCents;
      if (p.to === 'pending') Object.assign(next, { checkoutUrl: null, checkoutError: null });
      return next as unknown as EntityRecordMap[E];
    }
    case 'sav.create': {
      const p = m.payload;
      const d = lookup.dossier?.(p.dossierId);
      const rec: SavRecord = {
        id: m.entityId,
        version: 0,
        updatedAt: now,
        createdAt: now,
        reference: null,
        dossierId: p.dossierId,
        territoryId: d?.territoryId ?? '',
        status: actor.canValidateSav ? 'open' : 'pending_validation',
        reasonId: p.reasonId,
        description: p.description ?? null,
        installerId: null,
        installerName: null,
        interventionId: null,
        createdById: actor.userId,
        createdByName: actor.displayName,
        validatedByName: null,
        validatedAt: null,
        statusComment: null,
        causeId: null,
        closingComment: null,
        closedByName: null,
        closedAt: null,
      };
      return rec as EntityRecordMap[E];
    }
    case 'sav.transition': {
      if (!current) return current;
      const p = m.payload;
      const next = { ...(current as unknown as SavRecord), status: SAV_USER_TRANSITIONS[p.action]!.to };
      if (p.action === 'close')
        Object.assign(next, {
          causeId: p.causeId ?? null,
          closingComment: p.comment ?? null,
          closedByName: actor.displayName,
          closedAt: now,
        });
      if (p.action === 'reject' || p.action === 'cancel') next.statusComment = p.comment ?? null;
      if (p.action === 'validate')
        Object.assign(next, { validatedByName: actor.displayName, validatedAt: now });
      return next as unknown as EntityRecordMap[E];
    }
    case 'portal.revoke':
      return current ? ({ ...current, revokedAt: now } as EntityRecordMap[E]) : current;
    case 'prospect.contact': {
      if (!current) return current;
      const p = current as unknown as ProspectRecord;
      const at = m.payload.at ?? now;
      const status = p.clientId ? 'won' : statusAfter(m.payload.outcome);
      return {
        ...p,
        status,
        callbackOn: status === 'callback' ? (m.payload.callbackOn ?? null) : null,
        lastContactAt: at,
        lastOutcome: m.payload.outcome,
        contacts: [
          {
            id: m.mutationId,
            outcome: m.payload.outcome,
            comment: m.payload.comment ?? null,
            callbackOn: m.payload.callbackOn ?? null,
            at,
            byName: actor.displayName,
          },
          ...p.contacts,
        ],
      } as unknown as EntityRecordMap[E];
    }
    case 'prospect.link_client':
      return current
        ? ({
            ...current,
            clientId: m.payload.clientId,
            status: 'won',
            callbackOn: null,
          } as EntityRecordMap[E])
        : current;
    case 'notification.read':
    case 'announcement.read':
      return current ? ({ ...current, readAt: now } as EntityRecordMap[E]) : current;
    case 'file.update':
    case 'line.update': {
      if (!current) return current;
      const next = { ...current } as Record<string, unknown>;
      for (const [field, change] of Object.entries(m.changes)) next[field] = change.to;
      return next as unknown as EntityRecordMap[E];
    }
    case 'client.delete':
    case 'dossier.delete':
    case 'comment.delete':
    case 'line.delete':
    case 'file.delete':
      return undefined;
  }
}
